import {
  TB_GUEST_PLACEHOLDER_DOMAIN,
  TB_LEGACY_GUEST_DOMAIN,
  TB_PHONE_PLACEHOLDER_DOMAIN,
  resolvePlaceholderEmailDomains,
  type PlaceholderEmailDomains,
} from '../saas/lib/placeholderCustomerEmail';
import { fetchPlatformBusinessDefaults } from '../saas/lib/fetchOrgSettings';

/** Legacy / local placeholder suffixes (not platform-configurable). */
const LEGACY_PLACEHOLDER_SUFFIXES = [
  '@placeholder.local',
  '@temp.local',
  '@homecare.temp',
  '@phone.local',
  `@${TB_PHONE_PLACEHOLDER_DOMAIN}`,
  `@${TB_GUEST_PLACEHOLDER_DOMAIN}`,
  `@${TB_LEGACY_GUEST_DOMAIN}`,
] as const;

let runtimePlaceholderDomains: PlaceholderEmailDomains | null = null;

/** Load super-admin platform placeholder domains into client-side email checks. */
export function setRuntimePlaceholderEmailDomains(domains: PlaceholderEmailDomains | null): void {
  runtimePlaceholderDomains = domains;
}

export function getRuntimePlaceholderEmailDomains(): PlaceholderEmailDomains | null {
  return runtimePlaceholderDomains;
}

let runtimeDomainsLoadPromise: Promise<void> | null = null;

/** Fetch platform placeholder domains when bootstrap has not run yet (marketing / notifications). */
export async function ensureRuntimePlaceholderEmailDomains(): Promise<void> {
  if (runtimePlaceholderDomains) return;
  if (!runtimeDomainsLoadPromise) {
    runtimeDomainsLoadPromise = fetchPlatformBusinessDefaults()
      .then((defaults) => {
        setRuntimePlaceholderEmailDomains(resolvePlaceholderEmailDomains(defaults));
      })
      .catch(() => {
        /* static legacy suffix fallbacks remain active */
      })
      .finally(() => {
        runtimeDomainsLoadPromise = null;
      });
  }
  await runtimeDomainsLoadPromise;
}

/** True when address has valid format and is not a synthetic placeholder inbox. */
export async function isDeliverableCustomerEmail(email: string | null | undefined): Promise<boolean> {
  await ensureRuntimePlaceholderEmailDomains();
  const trimmed = (email || '').trim();
  if (!trimmed || !isValidEmailFormat(trimmed)) return false;
  return !isPlaceholderCustomerEmail(trimmed);
}

/** Build suffix list for placeholder detection (static + platform-configured domains). */
export function buildPlaceholderEmailSuffixes(domains?: PlaceholderEmailDomains | null): string[] {
  const resolved = domains ?? runtimePlaceholderDomains ?? resolvePlaceholderEmailDomains();
  return [...new Set([...LEGACY_PLACEHOLDER_SUFFIXES, `@${resolved.guest}`, `@${resolved.phone}`])];
}

/** Placeholder emails created when a customer has no real inbox (auth requirement). */
export function isPlaceholderCustomerEmail(
  email: string | null | undefined,
  domains?: PlaceholderEmailDomains | null,
): boolean {
  const normalized = (email || '').trim().toLowerCase();
  if (!normalized || !normalized.includes('@')) return true;
  return buildPlaceholderEmailSuffixes(domains).some((suffix) => normalized.endsWith(suffix));
}

/** Alias used in CRM surfaces for phone-only / guest auth emails. */
export const isGuestEmail = isPlaceholderCustomerEmail;

/** First non-placeholder email from the provided candidates. */
export function resolveRealCustomerEmail(...candidates: Array<string | null | undefined>): string {
  for (const candidate of candidates) {
    const email = (candidate || '').trim();
    if (email && !isPlaceholderCustomerEmail(email)) return email;
  }
  return '';
}

/** All unique real emails from candidates (preserves order). */
export function resolveRealCustomerEmails(
  ...candidates: Array<string | null | undefined | string[]>
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const candidate of candidates) {
    const list = Array.isArray(candidate) ? candidate : [candidate];
    for (const raw of list) {
      const email = (raw || '').trim();
      const key = email.toLowerCase();
      if (email && !isPlaceholderCustomerEmail(email) && !seen.has(key)) {
        seen.add(key);
        result.push(email);
      }
    }
  }
  return result;
}

export function isValidEmailFormat(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
}
